import { useQuery } from '@apollo/client';
import { ProgressIndicator, Stack, Text, makeStyles } from '@fluentui/react';
import { ToggleButton } from 'common/components/Buttons';
import { Chip } from 'common/components/Chip';
import { NoDataView } from 'common/components/DataPlaceholders';
import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  UserAccessGroups,
  UserAccessGroups_userGroups_nodes,
} from './__generated__/UserAccessGroups';

type UserGroupItems = UserAccessGroups_userGroups_nodes;

import { AccessGroupRow } from 'settings/account/userAccessPools/list';
import { AccessGroupValues } from '../types';
import USER_ACCESS_GROUPS from './UserAccessGroups.graphql';

const useStyles = makeStyles(() => ({
  container: {
    marginBottom: 200,
  },
  availableGroupItems: {
    maxHeight: 250,
    overflowY: 'scroll',
    overflowX: 'hidden',
    padding: '0px 20px',
  },
  heading: {
    marginLeft: 20,
  },
  progressContainer: {
    padding: '0px 20px',
  },
}));

interface GroupItemsProps {
  accessGroup?: AccessGroupRow;
  isUpdatable: boolean;
}

export const GroupItems: React.FC<GroupItemsProps> = ({
  accessGroup,
  isUpdatable,
}) => {
  const styles = useStyles();
  const [availableGroupItems, setAvailableGroupItems] = useState<
    UserGroupItems[]
  >([]);
  const [selectedGroupItems, setSelectedGroupItems] = useState<
    UserGroupItems[]
  >([]);

  const { id, accessGroupItems } = { ...accessGroup };

  const isEdit = !!id;

  const { control, setValue } = useFormContext<AccessGroupValues>();

  const watchAccessGroupItems = useWatch({
    name: 'accessGroupItems',
    control,
  });

  const { loading, data } = useQuery<UserAccessGroups>(USER_ACCESS_GROUPS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const onToggleClick = (accessGroup: UserGroupItems) => {
    if (!!accessGroup.id) {
      const isExists = watchAccessGroupItems?.some(
        (item) => item.userGroupId === accessGroup.id
      );
      if (!isExists) {
        setSelectedGroupItems([...selectedGroupItems, accessGroup]);
        setValue(
          'accessGroupItems',
          [...(watchAccessGroupItems || []), { userGroupId: accessGroup.id }],
          { shouldDirty: true }
        );
        const filteredArray = availableGroupItems.filter(
          (item) => item.id !== accessGroup.id
        );
        setAvailableGroupItems(filteredArray);
      }
    }
  };

  const onRemoveClick = (accessGroup: UserGroupItems) => {
    if (!!accessGroup.id) {
      const isExists = selectedGroupItems?.some(
        (item) => item.id === accessGroup.id
      );
      if (isExists) {
        const filteredArray = selectedGroupItems.filter(
          (item) => item.id !== accessGroup.id
        );
        setSelectedGroupItems(filteredArray);
        setAvailableGroupItems([accessGroup, ...availableGroupItems]);
        setValue(
          'accessGroupItems',
          [
            ...filteredArray.map((ele) => ({
              userGroupId: ele.id,
            })),
          ],
          { shouldDirty: true }
        );
      }
    }
  };

  useEffect(() => {
    const { nodes } = { ...data?.userGroups };
    const accessGroupArray: UserGroupItems[] =
      accessGroupItems?.nodes.map((item) => ({
        id: item.userGroupId,
        name: item.userGroup?.name || '',
      })) || [];
    if (!!accessGroupArray.length) {
      setSelectedGroupItems(accessGroupArray);
      const filteredArray =
        nodes?.filter((item) =>
          accessGroupArray.every((ele) => ele.id !== item.id)
        ) || [];
      if (!!nodes?.length) {
        setAvailableGroupItems(filteredArray);
      }
    } else {
      if (!!nodes?.length) {
        setAvailableGroupItems(nodes);
      }
    }
  }, [accessGroupItems, data, isEdit]);

  return (
    <Stack
      className={styles.container}
      tokens={{
        childrenGap: 20,
      }}
    >
      <Text variant="large" className={styles.heading}>
        Available User Groups
      </Text>
      {loading ? (
        <Stack className={styles.progressContainer}>
          <ProgressIndicator />
        </Stack>
      ) : (
        <Stack
          wrap
          horizontal
          tokens={{
            childrenGap: 10,
          }}
          className={styles.availableGroupItems}
        >
          {availableGroupItems?.map((item, index) => {
            return (
              <Stack key={index}>
                <ToggleButton
                  onClick={() => {
                    if (!!isUpdatable) onToggleClick(item);
                  }}
                  key={item.id}
                  text={item.name}
                  iconName={'Add'}
                />
              </Stack>
            );
          })}
          <Stack grow>
            <NoDataView
              show={!availableGroupItems?.length}
              title="No records available."
            />
          </Stack>
        </Stack>
      )}
      <Text variant="large" className={styles.heading}>
        Selected User Groups
      </Text>
      <Stack
        wrap
        horizontal
        tokens={{
          childrenGap: 10,
        }}
        className={styles.availableGroupItems}
      >
        {selectedGroupItems?.map((item, index) => {
          return (
            <Stack key={index}>
              <Chip
                text={item.name}
                onClick={() => onRemoveClick(item)}
                color={'RED'}
                disabled={!isUpdatable}
              />
            </Stack>
          );
        })}
        <Stack grow>
          <NoDataView
            show={!selectedGroupItems?.length}
            title="No records available"
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
